import { DepotKind, type Depot, type Header } from "../replay/format";

/*
 * What a structure is called (docs/ui-decisions.md, "One structure, one
 * glyph"): every structure is a depot in the frames (it holds its goods),
 * but players see a home, a station, an iron mine, a steelworks.
 */

/** The industry sites working from each station (depot index), in
 *  header order. */
export function sitesByStation(h: Header): Map<number, number[]> {
  const out = new Map<number, number[]>();
  (h.sites ?? []).forEach((s, i) => out.set(s.station, [...(out.get(s.station) ?? []), i]));
  return out;
}

/** The hexes within a mine's reach (`industry::MINE_RADIUS`, two), as
 *  offsets: nineteen. */
export const MINE_REACH: [number, number][] = [];
for (let dq = -2; dq <= 2; dq++) for (let dr = Math.max(-2, -dq - 2); dr <= Math.min(2, -dq + 2); dr++) MINE_REACH.push([dq, dr]);

const within2 = (a: [number, number], b: [number, number]) => Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[0] - b[0] + a[1] - b[1])) <= 2;

/** How many of the reach's hexes at (q, r) aren't within two of any of
 *  `others` (mines whose ground it would share). */
export function newGround(q: number, r: number, others: [number, number][]): number {
  return MINE_REACH.filter(([dq, dr]) => !others.some((o) => within2([q + dq, r + dr], o))).length;
}

/** Site `i` (a mine) in `h`: how many of its nineteen hexes it works, an
 *  older mine of the same ore taking the ones they share. */
export function mineWorks(h: Header, i: number, width: number): number {
  const sites = h.sites ?? [];
  const at = (t: number): [number, number] => [t % width, Math.floor(t / width)];
  const me = sites[i];
  const older = sites.slice(0, i).filter((o) => o.kind === "mine" && o.name === me.name && o.tile !== me.tile).map((o) => at(o.tile));
  const [q, r] = at(me.tile);
  return newGround(q, r, older);
}

/** Depot `index`'s name: a mine or plant by its site ("Iron mine",
 *  "Steelworks"), else by its kind. */
export function structureName(h: Header, depot: Depot, index: number): string {
  switch (depot.kind) {
    case DepotKind.Home:
      return "Home";
    case DepotKind.Station:
      return "Station";
    case DepotKind.Barracks:
      return "Barracks";
    case DepotKind.Field:
      return "Field depot";
    case DepotKind.Mine:
    case DepotKind.Plant: {
      const names = (h.sites ?? []).filter((s) => s.station === index).map((s) => s.name);
      // A mine digs every ore in its reach: one site an ore ("Iron ore
      // mine", "Coal mine") is "Iron + coal mine".
      if (depot.kind === DepotKind.Mine) {
        const ores = names.map((n) => n.replace(/( ore)? mine$/, ""));
        return ores.length ? `${ores.map((o, i) => (i ? o.toLowerCase() : o)).join(" + ")} mine` : "Mine";
      }
      return names.length ? names.join(", ") : "Plant";
    }
    default:
      return "Depot";
  }
}
